import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, FileSpreadsheet, Plus, Trash2, ArrowLeft, Loader2, Download } from "lucide-react";
import { extractCompanyPdf, parseCompanyExcel, createCompany, downloadExcelTemplate } from "@/lib/api";
import { toast } from "sonner";
import McaLookupWidget from "@/components/McaLookupWidget";
import DateField from "@/components/DateField";

const _newRowId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r${Math.random().toString(36).slice(2)}`);

const EMPTY_DIRECTOR = { name: "", din: "", designation: "Director", appointment_date: "", nationality: "Indian" };
const EMPTY_MEMBER = {
  name: "", email: "", member_type: "Equity", shares_held: 0, shareholding_percent: 0, address: "",
  folio_no: "", face_value: 0, father_name: "", pan_no: "", unique_id_no: "",
  occupation: "", nationality: "Indian", status: "Individual",
};
const EMPTY_PROFESSIONAL = {
  name: "", role: "Statutory Auditor", firm_name: "", membership_no: "",
  firm_registration_no: "", email: "", phone: "",
  appointment_date: "", term_end_date: "", notes: "",
};
const ROLE_OPTIONS = [
  "Statutory Auditor", "Internal Auditor", "Cost Auditor", "Secretarial Auditor",
  "Company Secretary", "Tax Advisor", "Legal Advisor", "Other",
];
const COMPANY_TYPE_OPTIONS = ["Private Limited", "Public Limited", "LLP", "OPC"];
const MEMBER_TYPE_OPTIONS = ["Equity", "Preference"];
const MEMBER_STATUS_OPTIONS = ["Individual", "Body Corporate", "Trust", "HUF", "LLP", "Partnership", "Government", "Other"];

export default function NewCompany() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("lookup"); // lookup | upload | manual
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    cin: "",
    company_type: "Private Limited",
    registered_address: "",
    email: "",
    incorporation_date: "",
    authorized_capital: "",
    paid_up_capital: "",
    directors: [{ ...EMPTY_DIRECTOR, _rid: _newRowId() }],
    members: [],
    professionals: [],
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await extractCompanyPdf(fd);
      const d = res.data;
      setForm({
        name: d.name || "",
        cin: d.cin || "",
        company_type: d.company_type || "Private Limited",
        registered_address: d.registered_address || "",
        email: d.email || "",
        incorporation_date: d.incorporation_date || "",
        authorized_capital: d.authorized_capital || "",
        paid_up_capital: d.paid_up_capital || "",
        directors: (d.directors && d.directors.length ? d.directors : [{ ...EMPTY_DIRECTOR }]).map((x) => ({
          name: x.name || "",
          din: x.din || "",
          designation: x.designation || "Director",
          appointment_date: x.appointment_date || "",
          nationality: x.nationality || "Indian",
          _rid: _newRowId(),
        })),
        members: (d.members || []).map((m) => ({ ...EMPTY_MEMBER, ...m, _rid: _newRowId() })),
      });
      setMode("manual");
      toast.success("MCA data extracted. Review and save.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      setUploading(false);
    }
  };

  const onExcelSingle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await parseCompanyExcel(fd);
      const list = res.data?.companies || [];
      if (list.length === 0) {
        toast.error("No company rows found in the file");
        return;
      }
      const d = list[0];
      setForm({
        name: d.name || "",
        cin: d.cin || "",
        company_type: d.company_type || "Private Limited",
        registered_address: d.registered_address || "",
        email: d.email || "",
        incorporation_date: d.incorporation_date || "",
        authorized_capital: d.authorized_capital || "",
        paid_up_capital: d.paid_up_capital || "",
        directors: (d.directors && d.directors.length ? d.directors : [{ ...EMPTY_DIRECTOR }]).map((x) => ({ ...EMPTY_DIRECTOR, ...x, _rid: _newRowId() })),
        members: (d.members || []).map((m) => ({ ...EMPTY_MEMBER, ...m, _rid: _newRowId() })),
        professionals: (d.professionals || []).map((p) => ({ ...EMPTY_PROFESSIONAL, ...p, _rid: _newRowId() })),
      });
      setMode("manual");
      if (list.length > 1) {
        toast.success(`First of ${list.length} rows loaded. Use Companies → Bulk Import for the rest.`);
      } else {
        toast.success("Excel data loaded. Review and save.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel parsing failed");
    } finally {
      setUploading(false);
    }
  };

  const onMcaPick = (rec) => {
    setForm((f) => ({
      ...f,
      name: rec.name || f.name,
      cin: rec.cin || f.cin,
      company_type: rec.company_type || f.company_type,
      registered_address: rec.registered_address || f.registered_address,
      incorporation_date: rec.incorporation_date || f.incorporation_date,
      authorized_capital: rec.authorized_capital || f.authorized_capital,
      paid_up_capital: rec.paid_up_capital || f.paid_up_capital,
    }));
    setMode("manual");
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDirector = (i, k, v) =>
    setForm((f) => ({ ...f, directors: f.directors.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));
  const addDirector = () => setForm((f) => ({ ...f, directors: [...f.directors, { ...EMPTY_DIRECTOR, _rid: _newRowId() }] }));  const removeDirector = (i) => setForm((f) => ({ ...f, directors: f.directors.filter((_, idx) => idx !== i) }));

  const setMember = (i, k, v) =>
    setForm((f) => ({ ...f, members: f.members.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)) }));
  const addMember = () => setForm((f) => ({ ...f, members: [...(f.members || []), { ...EMPTY_MEMBER, _rid: _newRowId() }] }));
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }));

  const setProfessional = (i, k, v) =>
    setForm((f) => ({ ...f, professionals: f.professionals.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const addProfessional = () => setForm((f) => ({ ...f, professionals: [...(f.professionals || []), { ...EMPTY_PROFESSIONAL, _rid: _newRowId() }] }));
  const removeProfessional = (i) => setForm((f) => ({ ...f, professionals: f.professionals.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      // Strip client-side row IDs before posting to the API.
      const stripRid = ({ _rid, ...rest }) => rest;
      const cleanedDirectors = form.directors.filter((d) => d.name?.trim()).map(stripRid);
      const cleanedMembers = (form.members || []).filter((m) => m.name?.trim()).map((m) => ({
        ...stripRid(m),
        shares_held: Number(m.shares_held) || 0,
        shareholding_percent: Number(m.shareholding_percent) || 0,
      }));
      const cleanedProfessionals = (form.professionals || []).filter((p) => p.name?.trim()).map(stripRid);
      const payload = {
        ...form,
        directors: cleanedDirectors,
        members: cleanedMembers,
        professionals: cleanedProfessionals,
      };
      const res = await createCompany(payload);
      toast.success("Company saved");
      navigate(`/companies/${res.data.id}`);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="new-company-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="label-eyebrow mb-2">Step 01 · Master Data</div>
      <h1 className="font-heading text-4xl font-light tracking-tight mb-8">Add Company</h1>

      {/* Mode switcher */}
      <div className="flex border border-zinc-300 mb-8 w-fit" data-testid="mode-switcher">
        <button
          onClick={() => setMode("lookup")}
          className={`px-5 py-2.5 text-sm font-medium ${mode === "lookup" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
          data-testid="mode-lookup"
        >
          MCA Lookup
        </button>
        <button
          onClick={() => setMode("upload")}
          className={`px-5 py-2.5 text-sm font-medium border-l border-zinc-300 ${mode === "upload" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
          data-testid="mode-upload"
        >
          Upload MCA PDF
        </button>
        <button
          onClick={() => setMode("excel")}
          className={`px-5 py-2.5 text-sm font-medium border-l border-zinc-300 ${mode === "excel" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
          data-testid="mode-excel"
        >
          Excel Upload
        </button>
        <button
          onClick={() => setMode("manual")}
          className={`px-5 py-2.5 text-sm font-medium border-l border-zinc-300 ${mode === "manual" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
          data-testid="mode-manual"
        >
          Manual Entry
        </button>
      </div>

      {mode === "lookup" && <McaLookupWidget onPick={onMcaPick} />}

      {mode === "upload" && (
        <label className="dropzone block p-16 text-center" data-testid="pdf-dropzone">
          {uploading ? (
            <div className="flex flex-col items-center gap-3 text-zinc-600">
              <Loader2 size={28} className="animate-spin text-[#002FA7]" />
              <span className="font-medium">Extracting via Claude Sonnet 4.5…</span>
              <span className="text-xs text-zinc-500">This usually takes 10-20 seconds.</span>
            </div>
          ) : (
            <>
              <Upload size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-3" />
              <div className="font-heading text-xl font-medium mb-1">Drop your MCA master data PDF</div>
              <div className="text-sm text-zinc-500 mb-4">We'll extract company, directors, CIN, capital — all editable before save.</div>
              <span className="swiss-btn-primary">
                <FileText size={14} /> Choose PDF File
              </span>
              <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="pdf-file-input" />
            </>
          )}
        </label>
      )}

      {mode === "excel" && (
        <div data-testid="excel-mode">
          <label className="dropzone block p-16 text-center" data-testid="excel-dropzone">
            {uploading ? (
              <div className="flex flex-col items-center gap-3 text-zinc-600">
                <Loader2 size={28} className="animate-spin text-[#002FA7]" />
                <span className="font-medium">Parsing Excel…</span>
              </div>
            ) : (
              <>
                <FileSpreadsheet size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-3" />
                <div className="font-heading text-xl font-medium mb-1">Drop your company master-data Excel</div>
                <div className="text-sm text-zinc-500 mb-4">
                  3 sheets — <strong>Companies</strong>, <strong>Directors</strong>, <strong>Members</strong> — linked by CIN.<br/>
                  First row pre-fills this form. For many companies at once, use <a href="/companies/import" className="text-[#002FA7] underline">Bulk Import</a>.
                </div>
                <span className="swiss-btn-primary">
                  <FileSpreadsheet size={14} /> Choose .xlsx File
                </span>
                <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onExcelSingle} data-testid="excel-file-input" />
              </>
            )}
          </label>
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadExcelTemplate();
                  toast.success("Template downloaded");
                } catch {
                  toast.error("Download failed");
                }
              }}
              className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
              data-testid="download-template"
            >
              <Download size={13} /> Download blank template (.xlsx)
            </button>
          </div>
        </div>
      )}

      {mode === "manual" && (
        <div className="space-y-10" data-testid="manual-form">
          {/* Company section */}
          <section className="border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-6 py-4">
              <div className="label-eyebrow">Company Details</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              <Field label="Company Name *" value={form.name} onChange={(v) => setField("name", v)} testId="field-name" />
              <Field label="CIN / LLPIN" value={form.cin} onChange={(v) => setField("cin", v)} testId="field-cin" placeholder="U12345MH2020PTC123456" />
              <SelectField
                label="Company Type"
                value={form.company_type}
                onChange={(v) => setField("company_type", v)}
                options={COMPANY_TYPE_OPTIONS}
                testId="field-type"
              />
              <DateField label="Incorporation Date" value={form.incorporation_date} onChange={(v) => setField("incorporation_date", v)} testId="field-incorp" />
              <Field label="Email" value={form.email} onChange={(v) => setField("email", v)} testId="field-email" />
              <Field label="Authorized Capital" value={form.authorized_capital} onChange={(v) => setField("authorized_capital", v)} testId="field-auth-cap" placeholder="INR 10,00,000" />
              <Field label="Paid-up Capital" value={form.paid_up_capital} onChange={(v) => setField("paid_up_capital", v)} testId="field-paid-cap" placeholder="INR 1,00,000" />
              <div className="md:col-span-2">
                <Field label="Registered Address" value={form.registered_address} onChange={(v) => setField("registered_address", v)} testId="field-address" textarea />
              </div>
            </div>
          </section>

          {/* Directors section */}
          <section className="border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <div className="label-eyebrow">Directors</div>
              <button onClick={addDirector} className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="add-director">
                <Plus size={14} /> Add Director
              </button>
            </div>
            <div className="divide-y divide-zinc-100">
              {form.directors.map((d, i) => (
                <div key={d._rid || `dir-${i}`} className="p-6" data-testid={`director-row-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="label-eyebrow">Director {i + 1}</span>
                    {form.directors.length > 1 && (
                      <button onClick={() => removeDirector(i)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-director-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Name" value={d.name} onChange={(v) => setDirector(i, "name", v)} testId={`director-name-${i}`} />
                    <Field label="DIN" value={d.din} onChange={(v) => setDirector(i, "din", v)} testId={`director-din-${i}`} />
                    <Field label="Designation" value={d.designation} onChange={(v) => setDirector(i, "designation", v)} testId={`director-desig-${i}`} />
                    <DateField label="Appointment Date" value={d.appointment_date} onChange={(v) => setDirector(i, "appointment_date", v)} testId={`director-appt-${i}`} />
                  </div>
                  <div className="mt-4 flex items-center gap-3 pt-3 border-t border-zinc-100">
                    <button
                      type="button"
                      onClick={addDirector}
                      className="px-3 py-1.5 border border-[#002FA7] text-[#002FA7] text-xs hover:bg-[#002FA7]/5 flex items-center gap-1"
                      data-testid={`director-${i}-add-another`}
                    >
                      <Plus size={12} /> Save & Add Another Director
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {form.directors.length > 0 && (
              <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50/40">
                <button
                  onClick={addDirector}
                  className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
                  data-testid="add-director-bottom"
                >
                  <Plus size={14} /> Add Another Director
                </button>
              </div>
            )}
          </section>

          {/* Members section */}
          <section className="border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Members / Shareholders</div>
                <p className="text-[11px] text-zinc-500 mt-1">Required for AGM/EGM notices and Shareholder Resolutions.</p>
              </div>
              <button onClick={addMember} className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="add-member">
                <Plus size={14} /> Add Member
              </button>
            </div>
            {(form.members || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No members added.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {form.members.map((m, i) => (
                  <div key={m._rid || `member-${i}`} className="p-6" data-testid={`member-row-${i}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="label-eyebrow">Member {i + 1}</span>
                      <button onClick={() => removeMember(i)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-member-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Name" value={m.name} onChange={(v) => setMember(i, "name", v)} testId={`member-name-${i}`} />
                      <Field label="Folio No." value={m.folio_no || ""} onChange={(v) => setMember(i, "folio_no", v)} testId={`member-folio-${i}`} />
                      <Field label="Email" value={m.email} onChange={(v) => setMember(i, "email", v)} testId={`member-email-${i}`} />
                      <SelectField label="Member Status" value={m.status || "Individual"} onChange={(v) => setMember(i, "status", v)} options={MEMBER_STATUS_OPTIONS} testId={`member-status-${i}`} />
                      <SelectField label="Share Class" value={m.member_type} onChange={(v) => setMember(i, "member_type", v)} options={MEMBER_TYPE_OPTIONS} testId={`member-type-${i}`} />
                      <Field label="Face Value per Share (INR)" value={m.face_value ?? 0} onChange={(v) => setMember(i, "face_value", v)} testId={`member-face-value-${i}`} />
                      <Field label="Shares Held" value={m.shares_held} onChange={(v) => setMember(i, "shares_held", v)} testId={`member-shares-${i}`} />
                      <Field label="Shareholding %" value={m.shareholding_percent} onChange={(v) => setMember(i, "shareholding_percent", v)} testId={`member-percent-${i}`} />
                      <Field label="Father's / Mother's / Spouse's Name" value={m.father_name || ""} onChange={(v) => setMember(i, "father_name", v)} testId={`member-father-${i}`} />
                      <Field label="Occupation" value={m.occupation || ""} onChange={(v) => setMember(i, "occupation", v)} testId={`member-occupation-${i}`} />
                      <Field label="PAN" value={m.pan_no || ""} onChange={(v) => setMember(i, "pan_no", v.toUpperCase())} testId={`member-pan-${i}`} />
                      <Field label="Unique ID (Aadhaar / Passport)" value={m.unique_id_no || ""} onChange={(v) => setMember(i, "unique_id_no", v)} testId={`member-uin-${i}`} />
                      <Field label="Nationality" value={m.nationality || "Indian"} onChange={(v) => setMember(i, "nationality", v)} testId={`member-nationality-${i}`} />
                      <div className="md:col-span-2">
                        <Field label="Address" value={m.address} onChange={(v) => setMember(i, "address", v)} testId={`member-address-${i}`} />
                      </div>
                    </div>
                    {/* Per-member footer actions — lets the user add the
                        next member without scrolling all the way to the top
                        of the form ("Add Member" button at the section header
                        is far away for long lists). */}
                    <div className="mt-4 flex items-center gap-3 pt-3 border-t border-zinc-100">
                      <button
                        type="button"
                        onClick={addMember}
                        className="px-3 py-1.5 border border-[#002FA7] text-[#002FA7] text-xs hover:bg-[#002FA7]/5 flex items-center gap-1"
                        data-testid={`member-${i}-add-another`}
                      >
                        <Plus size={12} /> Save & Add Another Member
                      </button>
                      <span className="text-[11px] text-zinc-400">
                        All members are saved together when you submit the company.
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Sticky footer "Add Member" so it's always reachable for long lists. */}
            {(form.members || []).length > 0 && (
              <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50/40">
                <button
                  onClick={addMember}
                  className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
                  data-testid="add-member-bottom"
                >
                  <Plus size={14} /> Add Another Member
                </button>
              </div>
            )}
          </section>

          {/* Professionals section */}
          <section className="border border-zinc-200 bg-white" data-testid="professionals-section">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Professionals</div>
                <p className="text-[11px] text-zinc-500 mt-1">Auditors (CA), Company Secretary, Tax Advisor, etc. Reused in resolutions and filings.</p>
              </div>
              <button onClick={addProfessional} className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="add-professional">
                <Plus size={14} /> Add Professional
              </button>
            </div>
            {(form.professionals || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500" data-testid="professionals-empty">No professionals added.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {form.professionals.map((p, i) => (
                  <div key={p._rid || `professional-${i}`} className="p-6" data-testid={`professional-row-${i}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="label-eyebrow">Professional {i + 1}</span>
                      <button onClick={() => removeProfessional(i)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-professional-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Name *" value={p.name} onChange={(v) => setProfessional(i, "name", v)} testId={`prof-name-${i}`} placeholder="e.g. CA Rajesh Sharma" />
                      <SelectField label="Role" value={p.role} onChange={(v) => setProfessional(i, "role", v)} options={ROLE_OPTIONS} testId={`prof-role-${i}`} />
                      <Field label="Firm Name" value={p.firm_name} onChange={(v) => setProfessional(i, "firm_name", v)} testId={`prof-firm-${i}`} placeholder="e.g. M/s ABC & Associates" />
                      <Field label="Membership No." value={p.membership_no} onChange={(v) => setProfessional(i, "membership_no", v)} testId={`prof-memno-${i}`} placeholder="ICAI / ICSI / ICMAI no." />
                      <Field label="Firm Registration No. (FRN)" value={p.firm_registration_no} onChange={(v) => setProfessional(i, "firm_registration_no", v)} testId={`prof-frn-${i}`} placeholder="e.g. 123456N" />
                      <Field label="Email" value={p.email} onChange={(v) => setProfessional(i, "email", v)} testId={`prof-email-${i}`} />
                      <Field label="Phone" value={p.phone} onChange={(v) => setProfessional(i, "phone", v)} testId={`prof-phone-${i}`} />
                      <DateField label="Appointment Date" value={p.appointment_date} onChange={(v) => setProfessional(i, "appointment_date", v)} testId={`prof-appt-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="flex justify-end gap-3">
            <button onClick={() => navigate(-1)} className="swiss-btn-ghost" data-testid="cancel-save">Cancel</button>
            <button onClick={save} disabled={saving} className="swiss-btn-primary" data-testid="save-company">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save Company
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder = "", textarea = false }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <Cmp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        rows={textarea ? 3 : undefined}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 focus:border-[#002FA7] bg-white"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
